import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { Layout } from "@/components/Layout/Layout";
import { MicrofrontendLoader } from "@/components/MicrofrontendLoader";
import { NotFound } from "@/components/NotFound";
import { Landing } from "@/pages/Landing";
import { Dashboard } from "@/pages/Dashboard";
import { Settings } from "@/pages/Settings";
import { SettingsAIApps } from "@/pages/SettingsAIApps";
import { SettingsUsers } from "@/pages/SettingsUsers";
import { SettingsAuditLogs } from "@/pages/SettingsAuditLogs";
import { SettingsOrganizations } from "@/pages/SettingsOrganizations";
import { SettingsUsage } from "@/pages/SettingsUsage";
import { SettingsUsageApp } from "@/pages/SettingsUsageApp";
import { SettingsUsageUser } from "@/pages/SettingsUsageUser";
import { Login } from "@/pages/Auth/Login";
import { useAuthStore } from "@/stores/authStore";
import { useUserApplications } from "@/hooks/useUserApplications";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { normalizeAppRoute } from "@/utils/routeUtils";
import  InsightSphere  from "@/pages/InsightSpehere";
import InsightSphereSupport from "@/pages/InsightSphereSupport";

export function AppRoutes() {
  const { checkAuth } = useAuthStore();
  const { applications, loading } = useUserApplications();
  const location = useLocation();

  const isStaticRoute = [
    "/",
    "/auth/login",
    "/dashboard",
    "/settings",
    "/settings/ai-apps",
    "/settings/users",
    "/settings/audit-logs",
    "/settings/organizations",
    "/settings/usage",
  ].includes(location.pathname) ||
    location.pathname.startsWith("/settings/usage/");

  const PUBLIC_ROUTES = [
    "/",
    "/auth/login",
    "/insight-sphere",
    "/insightsphere-support",
    "/deck-layout-planning",
  ];
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  // Check authentication on mount — skip for public routes
  useEffect(() => {
    if (isPublicRoute) return;
    checkAuth();
  }, [checkAuth, isPublicRoute]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth/login" element={<Login />} />
      <Route path="/insight-sphere" element={<InsightSphere />} />
      <Route path="/insightsphere-support" element={<InsightSphereSupport />} />

      {/* Public microfrontend route - Deck Layout Planning */}
      <Route
        path="/deck-layout-planning/*"
        element={
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center">
                <div className="text-sm text-gray-600">Loading application...</div>
              </div>
            }
          >
            <MicrofrontendLoader
              route="/deck-layout-planning"
              bundleUrl="https://d47d8jfslycan.cloudfront.net"
              appName="Deck Layout Planning"
            />
          </Suspense>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/ai-apps"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsAIApps />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/users"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsUsers />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/audit-logs"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsAuditLogs />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/organizations"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsOrganizations />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/usage"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsUsage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/usage/:appId"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsUsageApp />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/usage/:appId/users/:userId"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsUsageUser />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Dynamic microfrontend routes */}
      {applications.map((app) => (
        <Route
          key={app.id}
          path={`${app.route}/*`}
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense
                  fallback={
                    <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
                      <div className="text-sm text-gray-600">Loading application...</div>
                    </div>
                  }
                >
                  <MicrofrontendLoader
                    route={normalizeAppRoute(app.route)}
                    bundleUrl={app.app_url}
                    appName={app.appName}
                  />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          }
        />
      ))}

      {loading && !isStaticRoute && (
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Layout>
                <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
                  <div className="text-sm text-gray-600">Loading application...</div>
                </div>
              </Layout>
            </ProtectedRoute>
          }
        />
      )}

      {/* 404 fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
